import uniq from "lodash/uniq";

import * as types from '../actions/actionTypes';
import {getShareUrl, getUrlParam} from "../misc/helpers";
import {AVAILABLE_LANGUAGES, IS_LOOK_JOURNEY, IS_PRODUCT_JOURNEY, IS_LIXIL_UI} from "../config";
import COLOR_THEMES from "../colorThemes";
import {
  parseFabricatorSettings
} from "./fabricatorReducer";
import {WHITE_PAGE_ROUTES} from "../bvcomponents/look/core/constants";
import {
  hardCodeIsAmericanStandard,
  hardCodeIsAmericanImport,
  hardCodeIsCaesarStone,
  hardCodeIsModerno,
  hardCodeIsNhance, hardCodeIsRebath, hardCodeIsNFM, hardCodeIsStoneSystems
} from "../misc/fabricatorHardcode";
import {isTouchDevice} from "../misc/utils";
import Misc from "../misc/misc";
import {getMainThemeFromCSS} from "../loadingLogic";

const urlLanguage = (getUrlParam("language") || "").replace("/", "");
const urlTheme = (getUrlParam("theme") || "").replace("/", "");

const getDefaultTheme = () => {
  const theme = getMainThemeFromCSS();
  if (theme) {
    return theme
  }
  if (hardCodeIsCaesarStone()) {
    return "caes"
  } else if (hardCodeIsAmericanImport() || hardCodeIsAmericanStandard() || hardCodeIsRebath() || hardCodeIsStoneSystems()) {
    return "as"
  } else if (hardCodeIsModerno() || hardCodeIsNFM()) {
    return "moderno"
  } else if (hardCodeIsNhance()) {
    return "nhance"
  } else if (IS_PRODUCT_JOURNEY || window.location.href.includes("nav=product")) {
    return "grohe"
  } else if (IS_LOOK_JOURNEY || window.location.href.includes("nav=look")) {
    return "look"
  }
  return Object.keys(COLOR_THEMES)[0];
}

const activeTheme = urlTheme && COLOR_THEMES[urlTheme] ? urlTheme : getDefaultTheme();

const initialState = {
  bvData: {
    features: []
  },
  newQuoteData: {},
  groupedFeatures: {},
  groupedFeaturesDescriptions: {},
  windowWidth: window.innerWidth || 0,
  windowHeight: window.innerHeight || 0,
  isMobile: false,
  jobId: 0,
  stonePricing: {},
  documents: [],
  visualizersLoaded: [],
  cookieAccepted: false,
  successNotification: {
    show: false,
    icon: "calculate",
    title: "",
    message: ""
  },
  introVideo: {
    introVideoStarted: false,
    introVideoFinished: false,
  },
  shareUrl: "",
  javascriptErrors: [],
  language: urlLanguage && AVAILABLE_LANGUAGES.includes(urlLanguage) ? urlLanguage : "en-US",
  agreeEmailCheckbox: true,
  productionSheetCheckbox: false,
  productionSheetChangedShowPrices: false,
  networkConnection: true,
  activeTheme,
  amazonImageUrl: "",
  allFeaturesWereLoaded: [], // to check for lixil room layouts // includes kvState, bvLook, so on
  initialVisualizerType: "",
  allowRouting: false,
  allowMainLoading: !(window.location.hash === "#/shop" || window.location.hash.startsWith("#/shop/")),
  actionAfterRoomChange: null, // a function that runs after visualizer is changed
  visualizerIsChanging: false, // a boolean which is true when visualizer type is changing (from bathroom to kitchen fo example)
  initialRoute: window.location.hash,
  jobIsSaving: false,
};

export default function (state = initialState, action) {

  switch (action.type) {
    case types.SET_WINDOW_WIDTH: {
      if (IS_LIXIL_UI && action.payload.windowWidth >= 1024 && document.body.style.overflow === "hidden"
        && !(WHITE_PAGE_ROUTES.some(route => window.location.href.includes("#" + route)))) {
        document.body.style.overflow = "";
      }
      const isMobile = action.payload.windowWidth <= (IS_PRODUCT_JOURNEY || IS_LOOK_JOURNEY ? 1023 : 991);
      const isTouch = isMobile || Misc.isMobile() || isTouchDevice();
      isTouch ? document.body.classList.add("bv--touch") : document.body.classList.remove("bv--touch");
      return Object.assign({}, state, {
        isMobile,
        isTouch,
        ...action.payload,
      })
    }
    case types.SET_WINDOW_HEIGHT:
    case types.SET_COOKIE_ACCEPTED:
    case types.SET_LANGUAGE:
    case types.SET_AGREE_EMAIL_CHECKBOX:
    case types.SET_PRODUCTION_SHEET_CHECKBOX:
    case types.SET_PRODUCTION_SHEET_CHANGED_SHOW_PRICES:
    case types.SET_ACTIVE_THEME:
    case types.SET_NETWORK_CONNECTION:
    case types.SET_INITIAL_VISUALIZER_TYPE:
    case types.SET_AMAZON_IMAGE_URL:
    case types.SET_ALLOW_ROUTING:
    case types.SET_ALLOW_MAIN_LOADING:
    case types.SET_ACTION_AFTER_VISUALIZER_TYPE_CHANGED:
    case types.SET_VISUALIZER_TYPE_CHANGING:
    case types.SET_JOB_IS_SAVING:
      return Object.assign({}, state, {
        ...action.payload
      });
    case types.SET_STONE_PRICING:
      return Object.assign({}, state, {
        stonePricing: Object.assign({}, state.stonePricing, action.payload.stonePricing)
      });
    case types.SET_ALL_FEATURES_LOADED:
      if (state.allFeaturesWereLoaded.includes(action.payload.allFeaturesWereLoaded)) {
        return state
      }
      return Object.assign({}, state, {
        allFeaturesWereLoaded: [...state.allFeaturesWereLoaded, action.payload.allFeaturesWereLoaded]
      });
    case types.SET_JAVASCRIPT_ERRORS:
      return Object.assign({}, state, {
        javascriptErrors: uniq([...state.javascriptErrors, action.payload.javascriptError])
      });
    case types.SET_JOB_ID: {
      const shareUrl = getShareUrl(action.payload.jobId)
      window.shareUrl = shareUrl; // analytics only, we can't use getState() there
      return {
        ...state,
        shareUrl,
        jobId: action.payload.jobId,
      };
    }
    case types.TOGGLE_USER_POPUP:
      return handleToggleUserPopup(state, action.payload);
    case types.ADD_PROJECT_DOCUMENT:
    case types.SET_PROJECT_DOCUMENTS_IDS:
      return Object.assign({}, state, {
        documents: [...action.payload]
      });
    case types.ADD_VISUALIZERS_LOADED:
      return Object.assign({}, state, {
        visualizersLoaded: [...state.visualizersLoaded, action.payload.newLoadedVisualizer],
      });
    case types.SHOW_SUCCESS_NOTIFICATION:
      return Object.assign({}, state, {
        ...state, successNotification: {...state.successNotification, ...action.payload}
      });
    case types.SET_INTRO_VIDEO:
      return Object.assign({}, state, {
        ...state, introVideo: {...state.introVideo, ...action.payload}
      });
    case types.SET_FABRICATOR_DATA: {
      const {settings} = parseFabricatorSettings(action.payload);
      const language = settings.language;
      return Object.assign({}, state, {
        ...state, language: urlLanguage || (language && AVAILABLE_LANGUAGES.includes(language) ? language : state.language)
      });
    }
  }

  return state;

}

function handleToggleUserPopup(state, {userIsLoggedIn}) {
  if (typeof(userIsLoggedIn) != 'undefined' && !userIsLoggedIn) {
    return {...state, jobId: 0};
  }

  return state;
}
